/**
 * Allegati della segnalazione (QA26-F4.9): caricamento (mail .msg/.eml, PDF, foto, Office), elenco con le mail e i loro
 * allegati, anteprima di immagini e PDF nel portale, intestazione e testo delle mail. Contenuti oscurati nel feedback.
 */
import { App, Button, Card, List, Modal, Popconfirm, Space, Tag, Upload } from "antd";
import { FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FileTextOutlined, FileWordOutlined, InboxOutlined, MailOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { UploadFile } from "antd";
import { ALLEGATI_MAX_PER_INVIO, ALLEGATO_EST, ALLEGATO_MAX_BYTES, type AllegatoDto, type SegnalazioneDto } from "@qa/shared";
import { ANCORA } from "@qa/shared";
import { api, apiUrl } from "../../../api/client";
import { fmtDataOra } from "../../../lib/format";
import { useAggiorna } from "../../../lib/query";
import { messaggioErrore } from "../../../i18n/errori";
import { Vuoto } from "../../../components/ui";
import { palette } from "../../../theme";

const MB = Math.round(ALLEGATO_MAX_BYTES / 1024 / 1024);
const ACCEPT = ALLEGATO_EST.map((e) => `.${e}`).join(",");
const peso = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function Icona({ a }: { a: AllegatoDto }) {
  const st = { fontSize: 18, color: palette.muted };
  if (a.tipo === "mail") return <MailOutlined style={{ ...st, color: palette.primary }} />;
  if (a.tipo === "pdf") return <FilePdfOutlined style={{ ...st, color: palette.bad }} />;
  if (a.tipo === "immagine") return <FileImageOutlined style={{ ...st, color: palette.ok }} />;
  if (a.tipo === "testo") return <FileTextOutlined style={st} />;
  if (/\.(xlsx?|csv)$/i.test(a.nome)) return <FileExcelOutlined style={{ ...st, color: palette.ok }} />;
  if (/\.docx?$/i.test(a.nome)) return <FileWordOutlined style={{ ...st, color: palette.primary }} />;
  return <FileOutlined style={st} />;
}

/** Controllo prima dell'invio (lo stesso lo rifà il server). Ritorna il messaggio d'errore o null. */
export function controllaFile(f: File, t: TFunction): string | null {
  const ext = (f.name.split(".").pop() ?? "").toLowerCase();
  if (!ALLEGATO_EST.includes(ext)) return t("redflag.allegati.formato", { nome: f.name });
  if (f.size > ALLEGATO_MAX_BYTES) return t("redflag.allegati.troppoGrande", { nome: f.name, mb: MB });
  return null;
}

/** Invia i file alla segnalazione (un POST multipart). */
export function caricaAllegati(segnalazioneId: string, files: File[]) {
  const fd = new FormData();
  for (const f of files) fd.append("file", f, f.name);
  return api.post<AllegatoDto[]>(`/redflag/segnalazioni/${segnalazioneId}/allegati`, fd);
}

/** Selezione dei file senza invio immediato (form di nuova segnalazione). */
export function SceltaAllegati({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const lista: UploadFile[] = files.map((f, i) => ({ uid: `${i}-${f.name}`, name: f.name, size: f.size, status: "done" }));
  return (
    <Upload.Dragger
      multiple
      accept={ACCEPT}
      fileList={lista}
      beforeUpload={(f) => {
        const e = controllaFile(f, t);
        if (e) message.error(e);
        else if (files.length >= ALLEGATI_MAX_PER_INVIO) message.error(t("errori.allegato_troppi", { max: ALLEGATI_MAX_PER_INVIO }));
        else onChange([...files, f]);
        return false;
      }}
      onRemove={(u) => onChange(files.filter((_, i) => `${i}-${files[i]!.name}` !== u.uid))}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">{t("redflag.allegati.carica")}</p>
      <p className="ant-upload-hint">{t("redflag.allegati.caricaNota", { mb: MB, n: ALLEGATI_MAX_PER_INVIO })}</p>
    </Upload.Dragger>
  );
}

function Anteprima({ a, figli, onClose, onApri }: { a: AllegatoDto; figli: AllegatoDto[]; onClose: () => void; onApri: (x: AllegatoDto) => void }) {
  const { t } = useTranslation();
  const src = apiUrl(`/redflag/allegati/${a.id}/file`, { inline: 1 });
  return (
    <Modal
      open
      onCancel={onClose}
      title={a.nome}
      width={a.tipo === "pdf" ? 1000 : 820}
      footer={
        <Button href={apiUrl(`/redflag/allegati/${a.id}/file`)} target="_blank" rel="noopener">
          {t("redflag.allegati.scarica")}
        </Button>
      }
      destroyOnHidden
    >
      <div data-sensitive>
        {a.tipo === "immagine" && <img src={src} alt={a.nome} style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto" }} />}
        {a.tipo === "pdf" && <iframe title={a.nome} src={src} style={{ width: "100%", height: "72vh", border: `1px solid ${palette.hair}` }} />}
        {a.tipo === "mail" && a.mail && (
          <>
            <dl className="kv stretto">
              <dt>{t("redflag.allegati.da")}</dt>
              <dd>{a.mail.da ?? "—"}</dd>
              <dt>{t("redflag.allegati.a")}</dt>
              <dd>{a.mail.a ?? "—"}</dd>
              {a.mail.cc && (
                <>
                  <dt>{t("redflag.allegati.cc")}</dt>
                  <dd>{a.mail.cc}</dd>
                </>
              )}
              <dt>{t("redflag.allegati.data")}</dt>
              <dd>{a.mail.data ? fmtDataOra(a.mail.data) : "—"}</dd>
              <dt>{t("redflag.allegati.oggetto")}</dt>
              <dd>
                <b>{a.mail.oggetto ?? "—"}</b>
              </dd>
            </dl>
            <div className="muted small" style={{ marginTop: 10 }}>
              {t("redflag.allegati.corpo")}
            </div>
            <div style={{ whiteSpace: "pre-wrap", maxHeight: "45vh", overflow: "auto", background: palette.sunk, padding: 12, borderRadius: 6, marginTop: 4, fontSize: 13 }}>{a.mail.corpo ?? "—"}</div>
            {(figli.length > 0 || a.mail.nonEstratti.length > 0) && (
              <div style={{ marginTop: 12 }}>
                <div className="muted small">{t("redflag.allegati.allegatiMail")}</div>
                <Space wrap style={{ marginTop: 4 }}>
                  {figli.map((f) => (
                    <Button key={f.id} size="small" icon={<Icona a={f} />} onClick={() => onApri(f)}>
                      {f.nome}
                    </Button>
                  ))}
                </Space>
                {a.mail.nonEstratti.length > 0 && (
                  <div className="muted small" style={{ marginTop: 6 }}>
                    {t("redflag.allegati.nonEstratti", { elenco: a.mail.nonEstratti.map((x) => `${x.nome} (${t(`redflag.allegati.motivo_${x.motivo}`, { defaultValue: x.motivo })})`).join(", ") })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {(a.tipo === "office" || a.tipo === "testo" || (a.tipo === "mail" && !a.mail)) && <p className="muted">{t("redflag.allegati.anteprimaNo")}</p>}
      </div>
    </Modal>
  );
}

export function AllegatiCard({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [aperto, setAperto] = useState<AllegatoDto | null>(null);
  const radici = useMemo(() => r.allegati.filter((a) => !a.padreId || !r.allegati.some((x) => x.id === a.padreId)), [r.allegati]);
  const figliDi = (id: string) => r.allegati.filter((a) => a.padreId === id);

  const up = useMutation({
    mutationFn: (files: File[]) => caricaAllegati(r.id, files),
    onSuccess: (x) => {
      message.success(t("redflag.allegati.caricati", { n: x.length }));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const rm = useMutation({
    mutationFn: (id: string) => api.del<AllegatoDto[]>(`/redflag/allegati/${id}`),
    onSuccess: () => {
      message.success(t("redflag.allegati.rimosso"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const invia = (files: File[]) => {
    const ok = files.filter((f) => {
      const e = controllaFile(f, t);
      if (e) message.error(e);
      return !e;
    });
    if (ok.length) up.mutate(ok.slice(0, ALLEGATI_MAX_PER_INVIO));
  };

  const riga = (a: AllegatoDto, livello = 0) => {
    const figlio = livello > 0;
    return (
    <List.Item
      key={a.id}
      style={{ paddingLeft: livello * 28 }}
      actions={[
        <Button key="a" size="small" type="link" onClick={() => setAperto(a)}>
          {t("redflag.allegati.apri")}
        </Button>,
        <Button key="s" size="small" type="link" href={apiUrl(`/redflag/allegati/${a.id}/file`)} target="_blank" rel="noopener">
          {t("redflag.allegati.scarica")}
        </Button>,
        ...(r.puo.allegare && !figlio
          ? [
              <Popconfirm key="r" title={t("redflag.allegati.rimuoviConferma")} description={a.tipo === "mail" ? t("redflag.allegati.rimuoviMail") : undefined} okText={t("redflag.allegati.rimuovi")} cancelText={t("azioni.annulla")} onConfirm={() => rm.mutateAsync(a.id)}>
                <Button size="small" type="link" danger>
                  {t("redflag.allegati.rimuovi")}
                </Button>
              </Popconfirm>,
            ]
          : []),
      ]}
    >
      <List.Item.Meta
        avatar={<Icona a={a} />}
        title={
          <span data-sensitive>
            {a.nome} <span className="muted small">· {peso(a.dimensione)}</span>
          </span>
        }
        description={
          <span className="small" data-sensitive>
            {a.tipo === "mail" && a.mail ? (
              <>
                {a.mail.da ?? ""} · <b>{a.mail.oggetto ?? ""}</b>
                {a.mail.data ? ` · ${fmtDataOra(a.mail.data)}` : ""}
              </>
            ) : (
              t("redflag.allegati.caricatoDa", { chi: a.caricatoDa, quando: fmtDataOra(a.at) })
            )}
            {a.anteprima && (
              <Tag style={{ marginLeft: 6 }} bordered={false}>
                {a.tipo === "pdf" ? "PDF" : "IMG"}
              </Tag>
            )}
          </span>
        }
      />
    </List.Item>
    );
  };
  /** La mail e i suoi allegati, anche quelli di una mail dentro la mail. */
  const albero = (a: AllegatoDto, livello = 0): React.ReactNode[] => [riga(a, livello), ...figliDi(a.id).flatMap((f) => albero(f, livello + 1))];

  return (
    <Card size="small" id="allegati" title={`${t("redflag.allegati.titolo")}${r.allegati.length ? ` (${r.allegati.length})` : ""}`} style={{ marginTop: 16 }} data-tip={ANCORA.allegati}>
      {r.puo.allegare && (
        <Upload.Dragger
          multiple
          accept={ACCEPT}
          showUploadList={false}
          disabled={up.isPending}
          beforeUpload={(f, lista) => {
            // antd chiama beforeUpload per ogni file della scelta: si invia una volta sola, all'ultimo
            if (f === lista[lista.length - 1]) invia(lista as File[]);
            return false;
          }}
          style={{ marginBottom: 12 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{up.isPending ? t("redflag.allegati.caricando") : t("redflag.allegati.carica")}</p>
          <p className="ant-upload-hint">{t("redflag.allegati.caricaNota", { mb: MB, n: ALLEGATI_MAX_PER_INVIO })}</p>
        </Upload.Dragger>
      )}
      {radici.length ? (
        <List size="small" dataSource={radici} renderItem={(a) => albero(a)} />
      ) : (
        <Vuoto titolo={t("redflag.allegati.vuoto")} />
      )}
      {aperto && <Anteprima a={aperto} figli={figliDi(aperto.id)} onClose={() => setAperto(null)} onApri={setAperto} />}
    </Card>
  );
}
